import React from "react";

import {
  BlockFinancialIcon,
  RoadLoadRedIcon,
  ClockIcon,
  CheckIcon,
} from "@components/Icons";

import { ISalesOrder, IScheduledLoad } from "../../model";
import { KEY } from "@utils/enum";

export interface SaleOrderColumn {
  key: keyof ISalesOrder;
  label: string;
  align?: string;
  width?: string;
}

const flaggedHeader: SaleOrderColumn = {
  key: KEY.COMPARTIMENTS as keyof ISalesOrder,
  label: "Compartimentos",
  align: "center",
};

export const getTableHeaderScheduledLoadOrders = (
  featureFlaggProgrammingTransport: boolean
): SaleOrderColumn[] => {
  const headers = [
    {
      key: KEY.ORDER as keyof ISalesOrder,
      label: "Ordem de Venda",
    },
    {
      key: KEY.CNPJ as keyof ISalesOrder,
      label: "CNPJ/CPF",
    },
    {
      key: KEY.CUSTOMER as keyof ISalesOrder,
      label: "Cliente",
    },
    {
      key: KEY.PRODUCT as keyof ISalesOrder,
      label: "Produto",
    },
    {
      key: KEY.QUANTITY as keyof ISalesOrder,
      label: "Quantidade",
      align: "right",
    },
    {
      key: KEY.DELIVERY_DATE as keyof ISalesOrder,
      label: "Data de Entrega",
      align: "center",
    },
    {
      key: KEY.STATUS as keyof ISalesOrder,
      label: "Status",
      align: "center",
    },
  ];
  if (featureFlaggProgrammingTransport) {
    headers.push(flaggedHeader);
  }

  return headers;
};

export const tableHeaderUnscheduled = [
  {
    key: KEY.ICON,
    label: () => <ClockIcon style={{ width: 40, height: 40 }} />,
  },
  {
    key: KEY.CUSTOMER,
    label: "Cliente",
  },
  {
    key: KEY.CNPJ,
    label: "CNPJ/CPF",
  },
  {
    key: KEY.FILIAL_NAME,
    label: "Filial",
  },
  {
    key: KEY.PRODUCT,
    label: "Produto",
  },
  {
    key: KEY.QUANTITY,
    label: "Quantidade",
    align: "right",
  },
  {
    key: KEY.ORDER,
    label: "Ordem de venda",
    align: "right",
  },
  {
    key: KEY.FREIGHT,
    label: "Frete",
    align: "right",
  },
  {
    key: KEY.RELEASE_DATE,
    label: "Data de entrega",
    align: "center",
  },
];

/**
 * Retorna os headers para a tabela de agendados.
 * A coluna de "Status" (KEY.STATUS_PROGRAMATION) será incluída somente se a feature flag estiver ativa.
 */
export const getTableHeaderScheduled = (
  featureFlagProgrammingTransport: boolean
): {
  key: keyof IScheduledLoad;
  label: string | (() => JSX.Element);
  align?: string;
  width?: string;
}[] => {
  const headers = [
    {
      key: KEY.ICON as keyof IScheduledLoad,
      label: () => <RoadLoadRedIcon style={{ width: 40, height: 40 }} />,
    },
    {
      key: KEY.REF as keyof IScheduledLoad,
      label: "N° Agendamento",
      width: "fit-content",
      align: "center",
    },
    {
      key: KEY.TOTAL_AMOUNT_LITERS as keyof IScheduledLoad,
      label: "Volume Total (L)",
      align: "right",
      width: "fit-content",
    },
    {
      key: KEY.TRUCK_DRIVER as keyof IScheduledLoad,
      label: "Motorista",
    },
    {
      key: KEY.PLATE as keyof IScheduledLoad,
      label: "Placa",
    },
    {
      key: KEY.FREIGHT as keyof IScheduledLoad,
      label: "Frete",
      align: "right",
    },
    {
      key: KEY.BPID as keyof IScheduledLoad,
      label: "Transportadora",
      align: "right",
    },
    {
      key: KEY.FILIAL_NAME as keyof IScheduledLoad,
      label: "Filial",
    },
    {
      key: KEY.SALES_ORDER_QUANTITY as keyof IScheduledLoad,
      label: "Quant. Ordens de Venda",
      align: "center",
    },
    {
      key: KEY.SCHEDULE_DATE as keyof IScheduledLoad,
      label: "Agendado para",
      align: "center",
    },
  ];

  if (featureFlagProgrammingTransport) {
    headers.push({
      key: KEY.STATUS_PROGRAMATION as keyof IScheduledLoad,
      label: "Status",
      align: "center",
    });
  }

  return headers;
};

export const tableHeaderFinished: {
  key: keyof IScheduledLoad;
  label: string | (() => JSX.Element);
  align?: string;
  width?: string;
}[] = [
  {
    key: KEY.ICON as keyof IScheduledLoad,
    label: () => <CheckIcon style={{ width: 40, height: 40 }} />,
  },
  {
    key: KEY.REF as keyof IScheduledLoad,
    label: "N° Agendamento",
    width: "fit-content",
    align: "center",
  },
  {
    key: KEY.TOTAL_AMOUNT_LITERS as keyof IScheduledLoad,
    label: "Volume Total (L)",
    align: "right",
    width: "fit-content",
  },
  {
    key: KEY.TRUCK_DRIVER as keyof IScheduledLoad,
    label: "Motorista",
  },
  {
    key: KEY.PLATE as keyof IScheduledLoad,
    label: "Placa",
  },
  {
    key: KEY.FILIAL_NAME as keyof IScheduledLoad,
    label: "Filial",
  },
  {
    key: KEY.SALES_ORDER_QUANTITY as keyof IScheduledLoad,
    label: "Quant. Ordens de Venda",
    align: "center",
  },
  {
    key: KEY.FREIGHT as keyof IScheduledLoad,
    label: "Frete",
    align: "right",
  },
  {
    key: KEY.SCHEDULE_DATE as keyof IScheduledLoad,
    label: "Concluido",
    align: "left",
  },
];

export const tableHeaderCanceled: {
  key: keyof IScheduledLoad;
  label: string | (() => JSX.Element);
  align?: string;
  width?: string;
}[] = [
  {
    key: KEY.ICON as keyof IScheduledLoad,
    label: () => <RoadLoadRedIcon style={{ width: 40, height: 40 }} />,
  },
  {
    key: KEY.REF as keyof IScheduledLoad,
    label: "N° Agendamento",
    width: "fit-content",
    align: "center",
  },
  {
    key: KEY.TRUCK_DRIVER as keyof IScheduledLoad,
    label: "Motorista",
  },
  {
    key: KEY.FILIAL_NAME as keyof IScheduledLoad,
    label: "Filial",
    align: "left",
  },
  {
    key: KEY.REASON_CANCELLATION as keyof IScheduledLoad,
    label: "Motivo do Cancelamento",
    align: "center",
  },
  {
    key: KEY.TOTAL_AMOUNT_LITERS as keyof IScheduledLoad,
    label: "Quant. Total em L.",
    align: "right",
    width: "fit-content",
  },
  {
    key: KEY.SALES_ORDER_QUANTITY as keyof IScheduledLoad,
    label: "Quant. Ordens de Venda",
    align: "center",
  },
  {
    key: KEY.FREIGHT as keyof IScheduledLoad,
    label: "Frete",
    align: "right",
  },
  {
    key: KEY.SCHEDULE_DATE as keyof IScheduledLoad,
    label: "Foi Agendado em",
    align: "center",
  },
];

export const tableHeaderBlocked = [
  {
    key: KEY.ICON,
    label: () => <BlockFinancialIcon style={{ width: 40, height: 40 }} />,
  },
  {
    key: KEY.CUSTOMER,
    label: "Cliente",
  },
  {
    key: KEY.CNPJ,
    label: "CNPJ/CPF",
  },
  {
    key: KEY.FILIAL_NAME,
    label: "Filial",
  },
  {
    key: KEY.PRODUCT,
    label: "Produto",
  },
  {
    key: KEY.QUANTITY,
    label: "Quantidade",
    align: "right",
  },
  {
    key: KEY.ORDER,
    label: "Ordem de venda",
    align: "right",
  },
  {
    key: KEY.FREIGHT,
    label: "Frete",
    align: "right",
  },
  {
    key: KEY.BLOCKED_DATE,
    label: "Data de entrega",
    align: "center",
  },
];
